import React, { useCallback, useEffect, useState } from 'react';
import { Container, Typography, Box, Button, TextField, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { api } from '../../../api';

/**
 * Parents tab on a student's page (Oct 2026, Parent Access add-on). School admins (own school),
 * district admins and DiscloSure admins invite a parent by email or remove a linked parent. Only
 * shown as usable when the student's district has the add-on; the server enforces every rule.
 * Linked parents get view-only access to this student's account in the app.
 */
const ParentsTab = ({ data }) => {
  const theme = useTheme();
  const [state, setState] = useState({ loading: true, enabled: false, parents: [] });
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '' });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    const res = await api('get', { studentId: data.id }, 'parentlink');
    if (res?.status === 200 && res.data) {
      setState({ loading: false, enabled: Boolean(res.data.enabled), parents: res.data.parents || [] });
    } else {
      setState({ loading: false, enabled: false, parents: [] });
      setError(res?.data?.message || '');
    }
  }, [data.id]);

  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    setBusy(true); setError(''); setMessage('');
    const res = await api('post', { studentId: data.id, ...form }, 'parentlink');
    if (res?.status === 200) {
      setMessage(`Invite sent to ${form.email}. It expires in 7 days.`);
      setForm({ firstName: '', lastName: '', email: '' });
      await load();
    } else {
      setError(res?.data?.message || 'Could not send the invite. Please try again.');
    }
    setBusy(false);
  };

  const remove = async (parent) => {
    setBusy(true); setError(''); setMessage('');
    const res = await api('delete', { id: parent.id }, 'parentlink');
    if (res?.status === 200) {
      setMessage(`${parent.email} no longer has access.`);
      await load();
    } else {
      setError(res?.data?.message || 'Could not remove this parent. Please try again.');
    }
    setBusy(false);
  };

  const style = {
    wrap: { display: 'flex', flexDirection: 'column', gap: '16px', marginTop: '24px', marginBottom: '40px', marginLeft: '60px', marginRight: '60px', width: 'auto' },
    text: { fontSize: '14px', color: theme.palette.text?.secondary || '#666', lineHeight: 1.5 },
    row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '12px 0',
      borderBottom: `1px solid ${theme.palette.custom?.gray || '#eee'}` },
    name: { fontSize: '15px', fontWeight: 600, color: theme.palette.custom?.black || '#222' },
    form: { display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' },
    field: { minWidth: '200px', '& .MuiInputBase-input': { color: theme.palette.custom?.black || '#222' } },
    title: { fontSize: '18px', fontWeight: 700 },
    ok: { fontSize: '13px', color: theme.palette.success?.main || '#2e7d32' },
    error: { fontSize: '13px', color: theme.palette.error?.main || '#d32f2f' },
  };

  if (state.loading) return <Container sx={style.wrap}><CircularProgress size={24} /></Container>;

  if (!state.enabled) {
    return (
      <Container sx={style.wrap}>
        <Typography sx={style.title}>Parents</Typography>
        <Typography sx={style.text}>
          {error || "Parent Access isn't turned on for this school district. It's a paid add-on; contact DiscloSure to add it."}
        </Typography>
      </Container>
    );
  }

  const canSend = form.email.includes('@') && !busy;

  return (
    <Container sx={style.wrap}>
      <Typography sx={style.title}>Parents</Typography>
      <Typography sx={style.text}>
        A linked parent can view this student's DiscloSure account in the app. They can't submit, send or change
        anything, and reports involving home are always hidden from them. The student sees that a parent is linked.
      </Typography>

      {state.parents.length === 0 ? (
        <Typography sx={style.text}>No parents linked yet.</Typography>
      ) : (
        <Box>
          {state.parents.map((p) => (
            <Box key={p.id} sx={style.row}>
              <Box>
                <Typography sx={style.name}>{[p.firstName, p.lastName].filter(Boolean).join(' ') || p.email}</Typography>
                <Typography sx={style.text}>
                  {p.email}{p.invitedAt ? ` · invited ${new Date(p.invitedAt).toLocaleDateString()}` : ''}
                </Typography>
              </Box>
              <Button variant="outlined" color="error" disabled={busy} onClick={() => remove(p)}>Remove</Button>
            </Box>
          ))}
        </Box>
      )}

      <Typography sx={{ ...style.title, fontSize: '16px', marginTop: '8px' }}>Invite a parent</Typography>
      <Typography sx={style.text}>Use the parent's email from the school's records. They'll get an email with a temporary password.</Typography>
      <Box sx={style.form}>
        <TextField label="First name" size="small" sx={style.field} value={form.firstName}
          onChange={(e) => setForm({ ...form, firstName: e.target.value })} disabled={busy} />
        <TextField label="Last name" size="small" sx={style.field} value={form.lastName}
          onChange={(e) => setForm({ ...form, lastName: e.target.value })} disabled={busy} />
        <TextField label="Email" size="small" sx={style.field} value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={busy} />
        <Button variant="contained" disabled={!canSend} onClick={invite}>
          {busy ? <CircularProgress size={20} /> : 'Send invite'}
        </Button>
      </Box>
      {message ? <Typography sx={style.ok}>{message}</Typography> : null}
      {error ? <Typography sx={style.error}>{error}</Typography> : null}
    </Container>
  );
};

export default ParentsTab;
